import { Component, ChangeDetectionStrategy, input, output, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PostureRiskCheck } from '../../../../services/identity-security.service';
import { LinkedCasesPopupComponent } from '../linked-cases-popup/linked-cases-popup.component';

@Component({
  selector: 'app-risk-check-card',
  standalone: true,
  imports: [CommonModule, LinkedCasesPopupComponent],
  templateUrl: './risk-check-card.component.html',
  styleUrl: './risk-check-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RiskCheckCardComponent {
  riskCheck = input.required<PostureRiskCheck>();
  isExpanded = input<boolean>(false);
  acceptedCount = input<number>(0);
  toggle = output<string>();

  protected showCasesPopup = signal<boolean>(false);

  /** Left-accent color, kept in step with the shared severity-badge palette. */
  protected severityColor = computed(() => {
    switch (this.riskCheck().severity) {
      case 'Critical':
        return 'var(--severity-critical-fg)';
      case 'High':
        return 'var(--severity-high-fg)';
      case 'Medium':
        return 'var(--severity-medium-fg)';
      case 'Low':
        return 'var(--severity-low-fg)';
    }
  });

  protected failedCount = computed(() => this.riskCheck().failed_entity_count);

  /** True when the check could not be evaluated at all — no counter is meaningful. */
  protected notEvaluated = computed(() => this.riskCheck().status === 'insufficient_data');

  /**
   * What this check counts, in plain words. The card used to hardcode "users", but the
   * checks also count applications and service principals, so an over-privileged
   * applications card read "101 / 182 users protected".
   */
  protected entityNoun = computed(() => {
    switch (this.riskCheck().target_type) {
      case 'application':
        return 'applications';
      case 'service_principal':
        return 'service accounts';
      default:
        return 'users';
    }
  });

  /** Nothing in scope to assess — avoids rendering "0 / 0 users protected". */
  protected nothingInScope = computed(
    () => !this.notEvaluated() && this.riskCheck().total_entities === 0
  );

  /** Shared severity chip classes (same definition used on the dashboard tab). */
  protected severityBadgeClass = computed(
    () => `severity-badge severity-${(this.riskCheck().severity ?? 'Low').toLowerCase()}`
  );

  protected casesSubtitle = computed(() => {
    const count = this.riskCheck().linked_case_count;
    return `${count} case${count === 1 ? '' : 's'} linked to this risk check (last 30 days)`;
  });

  onToggle(): void {
    this.toggle.emit(this.riskCheck().check_id);
  }

  onViewCases(event: MouseEvent): void {
    event.stopPropagation();
    this.showCasesPopup.set(true);
  }

  onCloseCasesPopup(): void {
    this.showCasesPopup.set(false);
  }
}
